import {iconForReference} from '@github-ui/copilot-chat/components/ReferenceToken'
import {referenceID, referenceName} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {
  type CopilotChatReference,
  type DraftIssueProjectReference,
  type DraftIssueReference,
  type ExistingIssueReference,
  type FileDiffReference,
  type FileReference,
  type IssueReference,
  type JobReference,
  NullMessageId,
  type ThreadScopedFileReference,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {
  deserializeDraftIssueProjects,
  serializeDraftIssueProjects,
} from '@github-ui/copilot-chat/utils/draft-issue-serialization'
import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import type {RefObject} from 'react'
import type React from 'react'

export interface ContentPreviewContext {
  /** All items by ID, regardless of visibility. */
  items: ItemsMap
  /**
   * Given a base ID, get all the versions associated with it. For unversioned items this will be a single-element
   * array. Versions are sorted by timestamp.
   */
  versionedItems: VersionedItemsMap
  /**
   * Visible item IDs, in order. If the pane is closed, this will be an empty array.
   * Do not call this from a render method, as the component will not get updated when the items change.
   */
  getOpenItems: () => readonly PreviewableContentIdentifier[]
  /**
   * Open items, in order, even if the content preview pane is closed.
   */
  allOpenItems: readonly PreviewableContentIdentifier[]
  /** Item IDs that were visible before switching subthreads. Used to restore open files after new subthread loads */
  openItemsBeforeSubthreadChange: readonly PreviewableContentIdentifier[]
  /** Currently selected item. */
  selectedItem: PreviewableContentIdentifier | undefined
  /** Whether the loading UI is visible */
  showLoadingState: boolean
  /** Reference to the content preview container element */
  contentPreviewRef: RefObject<HTMLDivElement | null>
  /** Reference to the element that triggered opening the preview pane, used for returning focus */
  returnFocusElementRef: RefObject<HTMLElement | null>

  /** Add or update an item. */
  updateItem: (item: PreviewableContent) => void
  /** Set the items to restore after a subthread change. */
  setOpenItemsBeforeSubthreadChange: (items: readonly PreviewableContentIdentifier[]) => void
  /** Open the item, selecting it if specified. Does **not** open the pane if the pane is closed. */
  openItem: (id: PreviewableContentIdentifier, selectItem?: boolean) => void
  /** Close (hide) the item. */
  closeItem: (id: PreviewableContentIdentifier) => void
  /** Close (hide) the items with the given IDs, or all if undefined  */
  closeItems: (ids?: PreviewableContentIdentifier[]) => void
  /** Close the entire preview pane. Preserves open items for when reopened. */
  closePreviewPane: () => void
  /** Open the preview pane and restore any previously open items. Optionally specify an element to return focus to when closed. */
  openPreviewPane: (returnFocusElement?: HTMLElement | null) => void
  /** Close the resource panel. */
  closeResourcePanel: () => void
  /** Open the resource panel.  */
  openResourcePanel: () => void
  /** The repo selection requested via a repository reference click, if any. */
  resourcePanelRepoId: {repoId: number; ref?: string} | null
  /** Request the resource panel to select a specific repo (and optionally ref) by ID. */
  setResourcePanelRepoId: (selection: {repoId: number; ref?: string} | null) => void
  /** Directory path to expand in the file tree, set via tree URL clicks. */
  resourcePanelExpandedPath: string
  /** Set the directory path to expand in the file tree. */
  setResourcePanelExpandedPath: (path: string) => void
  /** Removes the items and openItems with the given IDs, or all if undefined  */
  removeItems: (ids?: PreviewableContentIdentifier[]) => void
  /** Shows the loading UI */
  enableLoadingState: () => void
  /** Hides the loading UI */
  disableLoadingState: () => void
  /** The currently ephemeral (temporary) tab, if any. Only one tab can be ephemeral at a time. */
  ephemeralTab: PreviewableContentIdentifier | undefined
  /** Open an item as an ephemeral tab, replacing any existing ephemeral tab. */
  openEphemeralItem: (id: PreviewableContentIdentifier) => void
  /** Promote an ephemeral tab to a permanent tab. */
  promoteTab: (id: PreviewableContentIdentifier) => void
  /** Monotonically increasing counter that changes whenever an item is selected or promoted. */
  itemSelectionVersion: number
}

export type PreviewableContentTypes =
  | 'file'
  | 'issue'
  | 'new-issue'
  | 'existing-issue'
  | 'update-issue'
  | 'image'
  | 'file-reference'
  | 'file-diff'
  | 'task-diff'
  | 'plugin-content'
  | 'job-result'

export type PreviewableContentIdentifier = `${PreviewableContentTypes}:${string}`

export type VersionedPreviewableContentIdentifier = `${PreviewableContentIdentifier}#${string}`

export interface PreviewableContentBase {
  messageId: string
  name: string
  id: PreviewableContentIdentifier
  type: PreviewableContentTypes
  disableCloseButton?: boolean
  trailingComponent?: React.ReactNode
}

export interface VersionedPreviewableContentBase extends PreviewableContentBase {
  id: VersionedPreviewableContentIdentifier
  /** Indicates whether the user has edited this unsaved/draft content */
  isUserEdited: boolean
  /** If present, indicates this item is a duplicate of another item with this ID (used to prevent version bumping) */
  duplicateOf?: PreviewableContentIdentifier
}

export interface File extends VersionedPreviewableContentBase {
  id: `file:${string}#${number}.${number}` | `file:${string}#${number}`
  language: string
  value: string
  type: 'file'
  name: string
  isStreaming: boolean
  timestamp: Date
  /** if this file is from a reference (i.e. not generated by Copilot) this will be set */
  reference?: FileReference | ThreadScopedFileReference
}

export interface FileDiff extends VersionedPreviewableContentBase {
  type: 'file-diff'
  id: `file-diff:${string}#${number}`
  baseFile: File
  headFile: File
  name: string
  language: string
  isStreaming: boolean
  timestamp: Date
  reference: FileDiffReference
}

export interface Issue extends PreviewableContentBase {
  number: number
  owner: string
  repo: string
  href: string
  type: 'issue'
  id: `issue:${string}`
  state?: 'open' | 'closed'
}

// A specific view of an existing issue, with additional
// properties to support use in drafting issue trees.
// The counterpart to a `DraftIssue` below.
export interface ExistingIssue extends PreviewableContentBase {
  type: 'existing-issue'
  id: `${ExistingIssue['type']}:${string}`
  tag: string
  parentTag?: string
  repository: string
  number: number
  state: 'open' | 'closed'
  href: string
  blockedByTags?: string[]
  isBlockingTags?: string[]
}

export interface DraftIssue extends VersionedPreviewableContentBase {
  /** The issue number/ID is not known until the issue is created, so we ask Copilot to create an identifier for us called a tag */
  tag: string
  parentTag?: string
  repository?: string
  type: 'new-issue' | 'update-issue'
  id: `${DraftIssue['type']}:${string}#${number}`
  template?: string
  body?: string
  bodyVersion?: string
  assignees?: string[]
  labels?: string[]
  issueType?: string
  projects?: DraftIssueProjectReference[]
  rawProjectsInput?: string[] // The raw, unfiltered list of projects from payload. TODO: https://github.com/github/copilot-voyager/issues/786
  milestone?: string
  isStreaming?: boolean
  defaultValuesById?: Record<string, string>
  /** If this field is present, the draft issue represents an update to an existing issue */
  issueNumber?: number
  blockedByTags?: string[]
  isBlockingTags?: string[]
  issueFields?: IssueFieldWithUnsavedValue[]
}

export type DraftOrExistingIssue = DraftIssue | ExistingIssue

export interface Image extends PreviewableContentBase {
  altText: string
  type: 'image'
  url: string
  id: `image:${string}`
}

export interface PluginContent extends PreviewableContentBase {
  type: 'plugin-content'
  reference: CopilotChatReference
  icon?: React.ComponentType
}

export interface JobResult extends PreviewableContentBase {
  type: 'job-result'
  id: `job-result:${string}`
  jobId: number
  workflowId?: number
  repoOwner: string
  repoName: string
}

export type VersionedPreviewableContent = File | DraftIssue | FileDiff

export type PreviewableContent = Issue | ExistingIssue | Image | VersionedPreviewableContent | PluginContent | JobResult

/**
 * A map of unversioned identifiers to a list of versioned identifiers.
 */
export type VersionedItemsMap = ReadonlyMap<PreviewableContentIdentifier, PreviewableContentIdentifier[]>

/**
 * A map of identifiers to the content they identify.
 */
export type ItemsMap = ReadonlyMap<PreviewableContentIdentifier, PreviewableContent>

export function stripVersionFromId<T extends PreviewableContentIdentifier>(id: T): T {
  return id.split('#')[0] as T
}

export function getRevisionNumber(
  id: PreviewableContentIdentifier,
  versionedItems: VersionedItemsMap,
  items: ItemsMap,
): number | null {
  // If this item is marked as a duplicate, resolve to the original item's version
  const item = items.get(id)
  if (item && 'duplicateOf' in item && item.duplicateOf) {
    return getRevisionNumber(item.duplicateOf, versionedItems, items)
  }

  const baseId = stripVersionFromId(id)
  const versions = versionedItems.get(baseId) ?? []
  const versionIndex = versions.indexOf(id)
  return versions.length > 1 && versionIndex !== -1 ? versionIndex + 1 : null
}

export function makeReferenceFromFile(item: File): ThreadScopedFileReference {
  return {
    type: 'thread-scoped-file',
    name: item.name,
    language: item.language,
    text: item.value,
  }
}

export function getItemIdFromThreadScopedFileReference(
  ref: ThreadScopedFileReference,
  messageIndex: number,
): File['id'] {
  return `file:${ref.name}#${messageIndex}` // TODO: this should be messageId once we have a stable value for that
}

export function getItemVersionForReference(
  ref: CopilotChatReference,
  messageIndex: number,
  versionedItems: VersionedItemsMap,
  items: ItemsMap,
): number | undefined {
  switch (ref.type) {
    case 'thread-scoped-file':
      return (
        getRevisionNumber(getItemIdFromThreadScopedFileReference(ref, messageIndex), versionedItems, items) ?? undefined
      )
    case 'draft-issue':
    case 'draft-update-issue':
      return (
        getRevisionNumber(makeVersionedItemFromReference({ref, messageIndex}).id, versionedItems, items) ?? undefined
      )
  }
}

export function makeItemFromThreadScopedFileReference(
  ref: ThreadScopedFileReference,
  messageIndex: number,
  messageId = NullMessageId,
  timestamp?: string,
): File {
  return {
    type: 'file',
    id: getItemIdFromThreadScopedFileReference(ref, messageIndex),
    reference: ref,
    messageId,
    name: ref.name,
    language: ref.language,
    value: ref.text,
    timestamp: timestamp ? new Date(timestamp) : new Date(),
    isStreaming: false,
    // If no message ID is provided, it's because the file has been edited and the message does not yet exist
    isUserEdited: messageId === NullMessageId,
  }
}

export function makeItemFromFileReference(ref: FileReference, messageId?: string, timestamp?: string): File {
  return {
    type: 'file',
    id: `file:${referenceID(ref)}#0`,
    reference: ref,
    messageId: messageId ?? NullMessageId,
    name: ref.path,
    language: ref.languageName ?? '',
    value: '',
    isStreaming: true,
    isUserEdited: false,
    timestamp: timestamp ? new Date(timestamp) : new Date(),
  }
}

export function makeItemFromFileDiffReference(
  ref: FileDiffReference,
  messageId?: string,
  timestamp?: string,
): FileDiff {
  // Create a default empty file for new/deleted files
  const createEmptyFile = (path: string): File => ({
    type: 'file',
    id: `file:empty:${path}#${Date.now()}`,
    messageId: messageId ?? NullMessageId,
    name: path,
    language: '',
    value: '',
    isStreaming: false,
    isUserEdited: false,
    timestamp: timestamp ? new Date(timestamp) : new Date(),
  })

  const baseFile = ref.baseFile ? makeItemFromFileReference(ref.baseFile) : createEmptyFile(ref.headFile?.path || '')
  const headFile = ref.headFile ? makeItemFromFileReference(ref.headFile) : createEmptyFile(ref.baseFile?.path || '')

  // Use the file that exists for metadata
  const existingFile = ref.headFile || ref.baseFile
  const filePath = existingFile?.path || ''

  return {
    type: 'file-diff',
    id: `file-diff:${referenceID(ref)}#0`,
    baseFile,
    headFile,
    name: filePath,
    language: (ref.headFile ? headFile : baseFile).language,
    isStreaming: true,
    isUserEdited: false,
    timestamp: timestamp ? new Date(timestamp) : new Date(),
    messageId: messageId ?? NullMessageId,
    reference: ref,
  }
}

export function makeItemFromIssueReference(ref: IssueReference, messageId?: string): Issue {
  return {
    type: 'issue',
    id: `issue:${ref.number}`,
    messageId: messageId ?? NullMessageId,
    name: ref.title ?? '',
    number: ref.number,
    owner: ref.repository.owner,
    repo: ref.repository.name,
    href: ref.url,
  }
}

export function makeItemFromJobReference(ref: JobReference, messageId?: string): JobResult {
  const {repoOwner, repoName, id} = ref
  return {
    type: 'job-result',
    id: `job-result:${repoOwner}-${repoName}-${id}`,
    messageId: messageId ?? NullMessageId,
    name: `Job #${id} in ${repoOwner}/${repoName}`,
    jobId: Number(id),
    workflowId: ref.workflow?.id,
    repoOwner,
    repoName,
  }
}

function makeReferenceFromDraftIssue(item: DraftIssue): DraftIssueReference {
  return {
    type: item.type === 'update-issue' ? 'draft-update-issue' : 'draft-issue',
    tag: item.tag,
    parentTag: item.parentTag,
    title: item.name,
    description: item.body,
    bodyVersion: item.bodyVersion,
    repository: item.repository,
    template: item.template,
    assignees: item.assignees,
    labels: item.labels,
    issueType: item.issueType,
    projects: serializeDraftIssueProjects(item.projects),
    rawProjectsInput: item.rawProjectsInput,
    milestone: item.milestone,
    templateFormValuesById: item.defaultValuesById,
    issueNumber: item.issueNumber,
    blockedByTags: item.blockedByTags,
    isBlockingTags: item.isBlockingTags,
    issueFields: item.issueFields,
  }
}

function makeItemFromDraftIssueReference({
  ref,
  messageId = NullMessageId,
  messageIndex,
}: {
  ref: DraftIssueReference
  messageIndex: number
  messageId?: string
}): DraftIssue {
  const type = ref.issueNumber ? 'update-issue' : 'new-issue'
  return {
    type,
    id: `${type}:${ref.tag}#${messageIndex}`,
    tag: ref.tag,
    parentTag: ref.parentTag,
    repository: ref.repository,
    name: ref.title,
    body: ref.description,
    bodyVersion: ref.bodyVersion,
    assignees: ref.assignees,
    labels: ref.labels,
    issueType: ref.issueType,
    projects: deserializeDraftIssueProjects(ref.projects),
    rawProjectsInput: ref.rawProjectsInput,
    milestone: ref.milestone,
    isUserEdited: messageId === NullMessageId, // If no message ID is provided, it's because the file has been edited and the message does not yet exist
    messageId,
    isStreaming: false,
    template: ref.template,
    defaultValuesById: ref.templateFormValuesById ?? {},
    issueNumber: ref.issueNumber,
    blockedByTags: ref.blockedByTags ?? [],
    isBlockingTags: ref.isBlockingTags ?? [],
    issueFields: ref.issueFields,
  }
}

export function makeItemForPluginSupportedReference(
  reference: CopilotChatReference,
  messageId = NullMessageId,
): PluginContent {
  return {
    type: 'plugin-content',
    id: `plugin-content:${reference.type}:${referenceID(reference)}`,
    name: referenceName(reference),
    icon: iconForReference(reference),
    messageId,
    reference,
  }
}

export function makeReferenceFromExistingIssue(item: ExistingIssue): ExistingIssueReference {
  return {
    type: item.type,
    tag: item.tag,
    parentTag: item.parentTag,
    repository: item.repository,
    number: item.number,
    title: item.name,
    state: item.state,
    url: item.href,
    blockedByTags: item.blockedByTags ?? [],
    isBlockingTags: item.isBlockingTags ?? [],
  }
}

export function makeReferenceFromVersionedItem(item: File): ThreadScopedFileReference
export function makeReferenceFromVersionedItem(item: DraftIssue): DraftIssueReference
export function makeReferenceFromVersionedItem(item: PreviewableContent): CopilotChatReference | null
export function makeReferenceFromVersionedItem(item: PreviewableContent): CopilotChatReference | null {
  switch (item.type) {
    case 'file':
      return makeReferenceFromFile(item)
    case 'new-issue':
    case 'update-issue':
      return makeReferenceFromDraftIssue(item)
    default:
      return null
  }
}

export function makeVersionedItemFromReference({
  ref,
  messageIndex,
  messageId,
  timestamp,
}: {
  ref: ThreadScopedFileReference
  messageIndex: number
  messageId?: string
  timestamp?: string
}): File
export function makeVersionedItemFromReference({
  ref,
  messageIndex,
  messageId,
}: {
  ref: DraftIssueReference
  messageIndex: number
  messageId?: string
}): DraftIssue
export function makeVersionedItemFromReference({
  ref,
  messageIndex,
}: {
  ref: CopilotChatReference
  messageIndex: number
  messageId?: string
  timestamp?: string
}): VersionedPreviewableContent | null
export function makeVersionedItemFromReference({
  ref,
  messageIndex,
  messageId,
  timestamp,
}: {
  ref: CopilotChatReference
  messageIndex: number
  messageId?: string
  timestamp?: string
}): VersionedPreviewableContent | null {
  switch (ref.type) {
    case 'thread-scoped-file':
      return makeItemFromThreadScopedFileReference(ref, messageIndex, messageId, timestamp)
    case 'draft-issue':
    case 'draft-update-issue':
      return makeItemFromDraftIssueReference({ref, messageIndex, messageId})
    default:
      return null
  }
}

export function getPreviousVersion<T extends PreviewableContent>(
  content: PreviewableContent,
  items: ItemsMap,
  versionedItems: VersionedItemsMap,
): T | undefined {
  const unversionedId = stripVersionFromId(content.id)
  const versions = versionedItems.get(unversionedId)
  const currentIdx = versions?.findIndex(v => v === content.id) ?? -1
  const previousVersionId = versions?.[currentIdx - 1]
  return previousVersionId && (items.get(previousVersionId) as T | undefined)
}

// Compares two draft issues to determine if they have meaningful content changes.
// Ignores fields that change on every render like messageId, isStreaming, and id.
export function areDraftIssuesEqual(a: DraftIssue, b: DraftIssue): boolean {
  const normalize = (issue: DraftIssue) => ({
    tag: issue.tag,
    parentTag: issue.parentTag,
    repository: issue.repository,
    type: issue.type,
    template: issue.template,
    name: issue.name,
    body: issue.body,
    bodyVersion: issue.bodyVersion,
    isUserEdited: issue.isUserEdited,
    issueType: issue.issueType,
    milestone: issue.milestone,
    issueNumber: issue.issueNumber,
    assignees: issue.assignees?.slice().sort(),
    labels: issue.labels?.slice().sort(),
    projects: issue.projects?.slice().sort((projectA, projectB) => {
      const aKey = `${projectA.id || ''}:${projectA.title || ''}`
      const bKey = `${projectB.id || ''}:${projectB.title || ''}`
      return aKey.localeCompare(bKey)
    }),
    rawProjectsInput: issue.rawProjectsInput?.slice().sort(),
    defaultValuesById: issue.defaultValuesById,
    blockedByTags: issue.blockedByTags?.slice().sort(),
    isBlockingTags: issue.isBlockingTags?.slice().sort(),
    issueFields: issue.issueFields
      ?.slice()
      .sort((fieldA, fieldB) => (fieldA.field.id ?? '').localeCompare(fieldB.field.id ?? '')),
  })

  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b))
}
